import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ISelect,
	ITableConfig,
	ITableHeader,
	ITableRow,
	ITableSortEvent,
	IToolbar,
	ListLayoutComponent,
	TableComponent,
	TableFilterComponent,
	TitleGroupComponent,
	ToolbarComponent,
} from '@tecalisdev/infinity';
import { Observable } from 'rxjs';
import { formatFilterTable, pruneEmptyFilterValues, transformTrimObject } from '../../../core/utils/utils';
import { PaginatedListController } from '../../../shared/list/paginated-list.controller';
import { LoadingService } from '../../../shared/services/loading.service';
import { ToolbarHelperService } from '../../../shared/services/toolbar-helper.service';
import { IUsersListPayload } from '../../../settings/pages/users/models/users.models';
import { UsersService } from '../../../settings/pages/users/services/users.service';
import { ActivityDetailModalComponent } from '../../components/activity-detail/activity-detail.component';
import { IUserActivityListItem, IUserActivityListResponse } from '../../models/user-activity.models';
import { ActivityHelperService } from '../../services/activity-helper.service';
import { UserActivityService } from '../../services/user-activity.service';

@Component({
	selector: 'app-activity-list',
	imports: [
		TranslatePipe,
		ListLayoutComponent,
		TitleGroupComponent,
		AlertComponent,
		ToolbarComponent,
		TableFilterComponent,
		TableComponent,
		ActivityDetailModalComponent,
	],
	templateUrl: './activity-list.component.html',
	styleUrl: './activity-list.component.scss',
})
export class ActivityListComponent extends PaginatedListController implements OnInit {
	private toolbarHelperService = inject(ToolbarHelperService);
	private activityService = inject(UserActivityService);
	private usersService = inject(UsersService);
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);

	public activityHelper = inject(ActivityHelperService);
	public toolbar: Signal<IToolbar>;
	public header: ITableHeader[] = this.activityHelper.header;
	public override config = this.activityHelper.config as ITableConfig;
	public content: ITableRow[] = [];
	public override filters: Record<string, unknown> = {};
	private sort: ITableSortEvent = this.activityHelper.sort;

	public showDetail = false;
	public activitySelected?: IUserActivityListItem | null = null;

	/** Evita getList duplicado cuando tec-toolbar re-emite el mismo formFilter. */
	private lastFilterFingerprint = this.buildFilterFingerprint({});

	ngOnInit(): void {
		this.toolbarHelperService.initializeListToolbar(this, () => this.activityHelper.toolbar, undefined, false, {
			skipInitialGetList: true,
		});
		this.loadUsersOptions();
		this.getList();

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.header = this.activityHelper.header;
			this.config = { ...this.activityHelper.config, pagination: this.config.pagination };
		});
	}

	/** Cancela la petición en curso en lugar del guard del base (filtros rápidos). */
	public override getList(): void {
		this.requestList?.unsubscribe();
		this.requestList = null;
		super.getList();
	}

	protected override beforeListRequest(): void {
		delete this.config.status;
		this.loading.showTable(this.config, this.header.length);
	}

	protected override afterListRequest(): void {
		this.loading.hideTable(this.config);
	}

	protected createListRequest(): Observable<IUserActivityListResponse> {
		this.getPayload();
		return this.activityService.list(this.filters);
	}

	protected applyListResponse(response: IUserActivityListResponse): void {
		this.content = this.activityHelper.transformRow(response.data);
		if (this.content.length === 0) {
			this.config = {
				...this.config,
				status: {
					type: 'notresults',
					title: this.trans.instant('ACTIVITY.NO_RESULTS'),
					description: this.trans.instant('ACTIVITY.NO_RESULTS_DESCRIPTION'),
				},
			};
		}
	}

	protected override onListError(_error: HttpErrorResponse): void {
		this.config = { ...this.config, status: { type: 'error' } };
		this.alertError = this.trans.instant('ERRORS.GENERIC_DESCRIPTION');
	}

	public sortChange(event: ITableSortEvent): void {
		if (!event.sortDirection) this.sort = this.activityHelper.resetSort();
		else this.sort = event;

		this.getList();
	}

	public override filterChange(filters: Record<string, unknown>): void {
		const fingerprint = this.buildFilterFingerprint(filters);
		if (fingerprint === this.lastFilterFingerprint) {
			return;
		}
		this.lastFilterFingerprint = fingerprint;

		this.filters = transformTrimObject(filters);
		this.setListConfigPagination(
			this.hasPrevPagination
				? { current_page: this.config?.pagination?.page, total: this.config?.pagination?.total }
				: { current_page: 1 },
		);
		this.hasPrevPagination = false;
		this.getList();
	}

	public closeDetail(): void {
		this.showDetail = false;
		this.activitySelected = null;
	}

	private getPayload(): void {
		this.formatFilters();
		this.filters = this.withPagination(this.filters, {
			sort_by: this.sort.sortKey,
			sort_order: this.sort.sortDirection,
		});
		this.toolbarHelperService.cleanSavedToolbar(this.constructor.name);
	}

	private formatFilters(): void {
		Object.keys(this.filters).forEach((key) => {
			const v = this.filters[key];
			if (v === '' || v === null || v === undefined) {
				delete this.filters[key];
			}
		});

		/** Pantalla “Términos legales” persiste actividad como `policy_checks` (API policy-check). */
		if (this.filters['type'] === 'legal_terms') {
			this.filters['type'] = 'policy_checks';
		}
	}

	/** Catálogo de usuarios del filtro: una sola carga, independiente del filtro de compañía. */
	private loadUsersOptions(): void {
		if (!this.activityHelper.canFilterByUser()) {
			this.activityHelper.setUserOptions([]);
			return;
		}

		const body: IUsersListPayload = {
			per_page: '1000',
			page: '1',
		};

		this.usersService
			.list(body)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					const options: ISelect[] = response.data.map((user) => ({
						id: user.uuid,
						label: [user.name, user.surname].filter(Boolean).join(' ').trim() || user.email,
					}));
					this.activityHelper.setUserOptions(options);
				},
				error: () => {
					this.activityHelper.setUserOptions([]);
				},
			});
	}

	/** Huella de filtros de usuario (sin paginación) para ignorar re-emits idénticos del toolbar. */
	private buildFilterFingerprint(raw: Record<string, unknown> | undefined): string {
		const normalized = formatFilterTable(transformTrimObject(raw ?? {})) as Record<string, unknown>;
		delete normalized['page'];
		delete normalized['per_page'];
		delete normalized['sort_by'];
		delete normalized['sort_order'];
		pruneEmptyFilterValues(normalized);

		const keys = Object.keys(normalized).sort();
		const sorted: Record<string, unknown> = {};
		for (const key of keys) {
			sorted[key] = normalized[key];
		}
		return JSON.stringify(sorted);
	}
}
